import { useEffect, useState } from 'react';
import useAnalyticsStore from '../../../../store/analyticsStore';
import useHomeStore from '../../../../store/homeStore';
import { useTimeSeriesQuery } from '../../../../queries/useTimeseriesQuery';
import { useRosePlotQuery } from '../../../../queries/useRosePlotQuery';
import { DEFAULT_ERROR_MESSAGE } from '../../../../api/Api';

interface UseAnalyticsDataParams {
  previewDatasetId: string;
  coordinates: number[];
  pointName: string;
  gridCellSize: string;
  waterDepth: string;
  point?: any;
  setDatasetIsOfflineWarningVisible: (visible: boolean) => void;
  isOffline: boolean;
  isFwe?: boolean;
  fwePointName?: string;
  metadata: any;
  orderId?: string;
}

export const useBasicAnalyticsData = (
  params: UseAnalyticsDataParams,
  { enabled = false }: { enabled?: boolean }
) => {
  const [timeSeriesPreview, setTimeSeriesPreview] = useState<any>(null);
  const [timeSeriesPreviewError, setTimeSeriesPreviewError] = useState('');
  const [scatterPlotPreview, setScatterPlotPreview] = useState<any>(null);
  const [scatterPlotPreviewError, setScatterPlotPreviewError] = useState('');
  const [rosePlotPreview, setRosePlotPreview] = useState<any>(null);
  const [rosePlotPreviewError, setRosePlotPreviewError] = useState('');
  const [defaultRosePlotDirection, setDefaultRosePlotDirection] = useState(1);

  const previewAnalytics = useAnalyticsStore((state) => state.previewAnalytics);
  const setPreviewAnalytics = useAnalyticsStore(
    (state) => state.setPreviewAnalytics
  );
  const timeStepInterval = useHomeStore((state) => state.timeStepInterval);

  const resetState = () => {
    setRosePlotPreviewError('');
    setTimeSeriesPreviewError('');
    setTimeSeriesPreview(null);
    setRosePlotPreview(null);
    setScatterPlotPreview(null);
    setScatterPlotPreviewError('');
  };

  const pointName = params.isFwe
    ? params?.fwePointName ?? params.pointName
    : params.pointName;

  const timeSeriesQuery = useTimeSeriesQuery(
    params.previewDatasetId,
    params.coordinates,
    pointName,
    timeStepInterval,
    params.metadata,
    params.orderId ?? '',
    enabled
  );

  const rosePlotQuery = useRosePlotQuery(
    params.previewDatasetId,
    params.coordinates,
    pointName,
    timeStepInterval,
    params.metadata,
    timeSeriesQuery.data?.data,
    timeSeriesQuery.isSuccess || timeSeriesQuery.isError,
    params.orderId ?? '',
    enabled
  );

  useEffect(() => {
    if (params.isOffline) {
      setTimeSeriesPreview(null);
      setTimeSeriesPreviewError(
        'Dataset is offline, please contact administrator'
      );
      setRosePlotPreview(null);
      setRosePlotPreviewError(
        'Dataset is offline, please contact administrator'
      );
      setScatterPlotPreview(null);
      setScatterPlotPreviewError(
        'Dataset is offline, please contact administrator'
      );
    }
  }, [params.isOffline]);

  useEffect(() => {
    if (timeSeriesQuery.data && !params.isOffline) {
      setTimeSeriesPreview(null);
      setTimeSeriesPreviewError('');
      setScatterPlotPreview(null);
      setScatterPlotPreviewError('');
      if (timeSeriesQuery.data.success) {
        setTimeSeriesPreview(timeSeriesQuery.data.data);
        setScatterPlotPreview(timeSeriesQuery.data.data);
      } else {
        setTimeSeriesPreviewError(timeSeriesQuery.data.error);
        setScatterPlotPreviewError(timeSeriesQuery.data.error);
      }
    }
  }, [timeSeriesQuery.data, params.previewDatasetId, params.isOffline]);

  useEffect(() => {
    if (rosePlotQuery.data) {
      setDefaultRosePlotDirection(1);
      setRosePlotPreview(null);
      setRosePlotPreviewError('');
      if (rosePlotQuery.data.direction) {
        setDefaultRosePlotDirection(rosePlotQuery.data.direction);
      }

      if (!rosePlotQuery.data.success) {
        setRosePlotPreviewError(
          rosePlotQuery.data.error || DEFAULT_ERROR_MESSAGE
        );
      } else {
        setRosePlotPreview(rosePlotQuery.data.data);

        if (timeSeriesQuery.data?.success) {
          setPreviewAnalytics([
            ...previewAnalytics,
            {
              coordinates: params.coordinates,
              data: timeSeriesQuery.data.data!.Result,
              datasetId: params.previewDatasetId,
              rosePlotPreviewdata: rosePlotQuery.data.data,
            },
          ]);
        }
      }
    }
  }, [
    rosePlotQuery.data,
    timeSeriesQuery.data,
    params.previewDatasetId,
    params.coordinates,
  ]);

  return {
    timeSeriesPreview,
    timeSeriesPreviewError,
    rosePlotPreview,
    rosePlotPreviewError,
    defaultRosePlotDirection,
    setTimeSeriesPreview,
    scatterPlotPreview,
    scatterPlotPreviewError,
    setRosePlotPreview,
    setScatterPlotPreview,

    loadingInitialAnalytics:
      timeSeriesQuery.isLoading || rosePlotQuery.isLoading,
    isTimeSeriesLoading: timeSeriesQuery.isLoading,
    isRosePlotLoading: rosePlotQuery.isLoading,

    timeSeriesError: timeSeriesQuery.error,
    rosePlotError: rosePlotQuery.error,

    isTimeSeriesSuccess: timeSeriesQuery.isSuccess,
    isRosePlotSuccess: rosePlotQuery.isSuccess,

    resetState,
  };
};
